import React from 'react';
import classNames from 'classnames';
import { useOnClickAway, useOnEscape } from '@/helpers';
import { Button, ButtonSize, ButtonVariant } from '../Button';
import { Icon, IconSize } from '../Icon';
import styles from './Dropdown.module.scss';

interface DropdownProps {
	className?: string;
	forceClosed?: boolean;
	buttonLabel: string | React.ReactElement;
	buttonVariant?: ButtonVariant | 'hasNoStyles';
	buttonSize?: ButtonSize;
	hasFullWidthButton?: boolean;
	iconSize?: IconSize;
	buttonClassName?: string;
	contentClassName?: string;
	alignContentRight?: boolean;
	children: React.ReactNode;
	ariaControls?: string;
	noRadius?: boolean;
}

/**
 * Generic Dropdown component, used for adding dropdown behavior to any type of dropdown.
 * Dropdown content is passed as children. Used in e.g. LanguageDropdown and FilterDropdown.
 */
export const Dropdown: React.FunctionComponent<DropdownProps> = ({
	className,
	forceClosed,
	buttonLabel,
	buttonVariant,
	buttonSize,
	hasFullWidthButton,
	iconSize,
	buttonClassName,
	contentClassName,
	alignContentRight,
	children,
	ariaControls,
	noRadius,
}) => {
	const wrapperRef = React.useRef<HTMLDivElement>(null);
	const [isOpen, setOpen] = React.useState(false);

	const handleToggle = (): void => {
		setOpen(!isOpen);
	};

	const handleClose = React.useCallback((): void => {
		setOpen(false);
	}, []);

	useOnClickAway(wrapperRef, handleClose, isOpen);
	useOnEscape(handleClose, isOpen);

	React.useEffect(() => {
		if (forceClosed) {
			setOpen(false);
		}
	}, [forceClosed]);

	return (
		<div className={classNames(styles.wrapper, className, { [styles.isOpen]: isOpen })} ref={wrapperRef}>
			<Button
				aria-expanded={isOpen}
				aria-controls={ariaControls}
				className={classNames(styles.button, buttonClassName)}
				variant={buttonVariant !== 'hasNoStyles' ? buttonVariant : undefined}
				size={buttonSize}
				noRadius={noRadius}
				hasNoStyles={buttonVariant === 'hasNoStyles'}
				onClick={handleToggle}
				isFullWidth={hasFullWidthButton}
			>
				{typeof buttonLabel === 'string' ? <span>{buttonLabel}</span> : <>{buttonLabel}</>}
				<Icon className={'u-ml-3xs'} name={'arrowLeft'} rotate={'270'} size={iconSize || 'sm'} />
			</Button>
			{isOpen && (
				<div
					id={ariaControls}
					className={classNames(
						styles.dropdownContent,
						{ [styles.isRightAligned]: alignContentRight },
						contentClassName,
					)}
				>
					{children}
				</div>
			)}
		</div>
	);
};
